import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { memo, useContext, useEffect, useRef, type ReactNode } from "react";
import { TabNavigationContext } from "../setting-section/tab-navigation-provider";
import { useCompactSettings } from "./settings-density-context";
import { SettingsHelpIcon } from "./settings-help-icon";
import * as css from "./settings-rows.module.css";

/** Anchor id for a section, derived from its title. */
export function settingsSectionId(title: string): string {
  return `section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

const NO_ERRORS: string[] = [];

/** Titled block of settings that registers itself with the jump nav. */
export const SettingsSection = memo(function SettingsSection({
  title,
  description,
  helpText,
  action,
  alignActionWithTitle = false,
  hideHeader = false,
  errors = NO_ERRORS,
  children,
}: {
  title: string;
  description?: ReactNode;
  helpText?: string;
  action?: ReactNode;
  /** Puts the action on the title line, centered with the title, not at the top of the header. */
  alignActionWithTitle?: boolean;
  /** The content supplies its own heading, such as an encapsulated table card. */
  hideHeader?: boolean;
  /** Shown as the error dot on this section's jump nav item. */
  errors?: string[];
  children: ReactNode;
}) {
  const compact = useCompactSettings();
  const id = settingsSectionId(title);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const tabNavigation = useContext(TabNavigationContext);
  const addSectionRef = tabNavigation?.addSectionRef;
  const removeSectionRef = tabNavigation?.removeSectionRef;
  const setSectionErrors = tabNavigation?.setSectionErrors;
  useEffect(() => {
    setSectionErrors?.(id, errors);
    // Same unstable provider callbacks as the registration below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, errors]);
  useEffect(() => {
    if (!addSectionRef || !removeSectionRef) {
      return;
    }
    addSectionRef(id, title, sectionRef.current);
    return () => removeSectionRef(id);
    // Provider callbacks change every render, as in setting-section.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, title]);
  const titleNode = (
    <Flex align="center" gap="sm">
      <Text
        fontSize={compact ? "md" : "lg"}
        fontWeight={compact ? "medium" : "semibold"}
        textColor="primary"
      >
        {title}
      </Text>
      {helpText && <SettingsHelpIcon text={helpText} />}
    </Flex>
  );
  const descriptionNode = description ? (
    <Text
      fontSize={compact ? "xs" : "sm"}
      fontWeight="regular"
      textColor="tertiary"
    >
      {description}
    </Text>
  ) : null;
  return (
    <Flex
      as="section"
      className={css.section}
      data-settings-section={title}
      dir="column"
      gap={compact ? "xl" : "2xl"}
      id={id}
      ref={sectionRef}
      w="full"
    >
      {!hideHeader &&
        (alignActionWithTitle ? (
          <Flex
            dir="column"
            gap={compact ? "xs" : "xxs"}
            px={compact ? "none" : "2xl"}
          >
            <Flex align="center" gap="xl" justify="space-between">
              {titleNode}
              {action && <Flex shrink={0}>{action}</Flex>}
            </Flex>
            {descriptionNode}
          </Flex>
        ) : (
          <Flex
            align="flex-start"
            gap="xl"
            justify="space-between"
            px={compact ? "none" : "2xl"}
          >
            <Flex dir="column" gap={compact ? "xs" : "xxs"}>
              {titleNode}
              {descriptionNode}
            </Flex>
            {action && <Flex shrink={0}>{action}</Flex>}
          </Flex>
        ))}
      {children}
    </Flex>
  );
});
